import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  CheckCircle, XCircle, Coins, ChevronRight, Zap, Info,
  CreditCard, Lock, Sparkles, ShieldCheck, Copy, ImagePlus,
  Clock, Hash, RefreshCw, AlertCircle, ArrowLeft,
} from "lucide-react";
import { L, t } from "../lib/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN_TOPUP_ILS = 25;
const QUICK_AMOUNTS = [25, 50, 100, 200, 300, 500];
const IFRAME_ID = "allpay-iframe";
const BITPAY_URL = "https://www.bitpay.co.il/app/me/19BE4B0E-FEB9-E532-019B-FDB4F7F9286F5E69";
const PUB = process.env.PUBLIC_URL || "";
const BIT_LOGO = `${PUB}/payment/bit-logo.png`;   // official bit app logo
const CARD_IMG = `${PUB}/payment/card.png`;        // realistic 3D card render

const fmtIP = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const fmtIls = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// ── Modals ────────────────────────────────────────────────────────────────────

const MODAL_STYLES = `
@keyframes modal-backdrop-in { from { opacity: 0 } to { opacity: 1 } }
@keyframes modal-sheet-in    { from { transform: translateY(32px) scale(0.97); opacity: 0 } to { transform: translateY(0) scale(1); opacity: 1 } }
@keyframes pulse-ring        { 0%,100% { transform: scale(1);   opacity: 0.6 } 50% { transform: scale(1.18); opacity: 0 } }
@keyframes spin-slow         { to { transform: rotate(360deg) } }
@keyframes float-up          { 0% { opacity:1; transform: translateY(0) scale(1) } 100% { opacity:0; transform: translateY(-56px) scale(0.7) } }
`;

function Modal({ children, onClose, accent = "#8B5CF6" }) {
  return (
    <>
      <style>{MODAL_STYLES}</style>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
        style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(12px)", animation: "modal-backdrop-in 200ms ease" }}
        onClick={e => e.target === e.currentTarget && onClose?.()}>
        <div className="w-full sm:max-w-[400px] sm:mx-4 sm:mb-0 mb-0 rounded-t-[28px] sm:rounded-[28px] overflow-hidden"
          style={{
            background: "var(--bg-primary)",
            border: "1px solid var(--border-color)",
            boxShadow: `0 0 0 1px rgba(255,255,255,0.04) inset, 0 40px 100px rgba(0,0,0,0.65)`,
            animation: "modal-sheet-in 280ms cubic-bezier(0.34,1.56,0.64,1)",
          }}>
          {/* Drag handle on mobile */}
          <div className="flex justify-center pt-3 pb-1 sm:hidden">
            <div className="w-10 h-1 rounded-full" style={{ background: "var(--border-color)" }} />
          </div>
          {children}
        </div>
      </div>
    </>
  );
}

function SuccessModal({ lang, ipoints, amountIls, onDone }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const target = Number(ipoints) || 0;
    if (!target) { setCount(target); return; }
    let start = null;
    const dur = 1200;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / dur, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(ease * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [ipoints]);

  return (
    <Modal accent="#22c55e">
      {/* Glow bar */}
      <div style={{ height: 3, background: "linear-gradient(90deg,#22c55e,#4ade80,#22c55e)", backgroundSize: "200% 100%", animation: "pulse-ring 2s ease infinite" }} />
      <div className="px-6 pb-7 pt-5 text-center">
        {/* Icon */}
        <div className="relative w-20 h-20 mx-auto mb-5 flex items-center justify-center">
          <div className="absolute inset-0 rounded-full" style={{ background: "rgba(34,197,94,0.12)", animation: "pulse-ring 2.4s ease infinite" }} />
          <div className="absolute inset-2 rounded-full" style={{ background: "rgba(34,197,94,0.08)", animation: "pulse-ring 2.4s ease 0.4s infinite" }} />
          <div className="relative w-14 h-14 rounded-full flex items-center justify-center"
            style={{ background: "linear-gradient(135deg,#22c55e,#16a34a)", boxShadow: "0 8px 32px rgba(34,197,94,0.45)" }}>
            <CheckCircle className="w-7 h-7 text-white" strokeWidth={2.5} />
          </div>
        </div>

        <h2 className="text-2xl font-black font-outfit mb-1.5" style={{ color: "var(--text-primary)" }}>
          {L(lang, "Payment Successful!", "Оплата прошла!", "!התשלום הצליח")}
        </h2>
        <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
          {L(lang, "iPoints have been added to your account", "iPoints зачислены на ваш счёт", "iPoints נוספו לחשבון שלך")}
        </p>

        {/* Big iPoints counter */}
        <div className="rounded-3xl py-5 px-4 mb-5 relative overflow-hidden"
          style={{ background: "linear-gradient(145deg, rgba(34,197,94,0.07), rgba(34,197,94,0.03))", border: "1.5px solid rgba(34,197,94,0.18)" }}>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: "rgba(34,197,94,0.7)" }}>
            {L(lang, "iPoints credited", "Начислено iPoints", "iPoints שזוכו")}
          </div>
          <div className="font-black font-outfit leading-none" style={{ fontSize: "clamp(2.4rem, 10vw, 3.2rem)", color: "#22c55e", textShadow: "0 0 40px rgba(34,197,94,0.3)" }}>
            +{count.toLocaleString()}
            <span className="text-xl ms-2 font-bold" style={{ opacity: 0.6 }}>iP</span>
          </div>
          <div className="mt-2 text-xs font-semibold" style={{ color: "var(--text-secondary)" }}>
            {L(lang, "Paid", "Оплачено", "שולם")} <span className="font-bold" style={{ color: "var(--text-primary)" }}>₪{fmtIls(amountIls)}</span>
          </div>
        </div>

        <button onClick={onDone} className="w-full font-bold text-sm flex items-center justify-center gap-2 press-btn"
          style={{ height: 52, borderRadius: 9999, background: "linear-gradient(135deg,#22c55e,#16a34a)", color: "#fff", boxShadow: "0 6px 24px rgba(34,197,94,0.35)", fontSize: 15 }}>
          {L(lang, "Go to Dashboard", "В личный кабинет", "לדשבורד")}
          <ChevronRight className="w-4 h-4 rtl-flip" />
        </button>
      </div>
    </Modal>
  );
}

function PendingModal({ lang, refCode, ipoints, onDone }) {
  return (
    <Modal accent="#8B5CF6">
      <div style={{ height: 3, background: "linear-gradient(90deg,#8B5CF6,#a78bfa,#8B5CF6)" }} />
      <div className="px-6 pb-7 pt-5 text-center">
        {/* Spinning clock icon */}
        <div className="relative w-20 h-20 mx-auto mb-5 flex items-center justify-center">
          <div className="absolute inset-0 rounded-full border-2 border-dashed" style={{ borderColor: "rgba(139,92,246,0.25)", animation: "spin-slow 8s linear infinite" }} />
          <div className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ background: "linear-gradient(135deg,#8B5CF6,#4F46E5)", boxShadow: "0 8px 32px rgba(139,92,246,0.4)" }}>
            <Clock className="w-7 h-7 text-white" strokeWidth={2} />
          </div>
        </div>

        <h2 className="text-2xl font-black font-outfit mb-1.5" style={{ color: "var(--text-primary)" }}>
          {L(lang, "Under Review", "На проверке", "בבדיקה")}
        </h2>
        <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
          {L(lang,
            "We received your proof. iPoints will be credited once verified.",
            "Мы получили чек. iPoints зачислятся после проверки.",
            "קיבלנו את האישור. iPoints יזוכו לאחר אימות."
          )}
        </p>

        {/* Info card */}
        <div className="rounded-2xl overflow-hidden mb-5" style={{ border: "1.5px solid rgba(139,92,246,0.2)", background: "rgba(139,92,246,0.05)" }}>
          {refCode && (
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid rgba(139,92,246,0.12)" }}>
              <span className="text-xs font-semibold" style={{ color: "var(--text-secondary)" }}>
                {L(lang, "Reference code", "Код платежа", "קוד תשלום")}
              </span>
              <span className="font-black font-outfit text-sm" style={{ color: "#a78bfa", letterSpacing: "0.12em" }}>{refCode}</span>
            </div>
          )}
          <div className="flex items-center justify-between px-4 py-3.5">
            <span className="text-sm font-semibold" style={{ color: "var(--text-secondary)" }}>
              {L(lang, "Expected", "Ожидается", "צפוי")}
            </span>
            <span className="text-xl font-black font-outfit" style={{ color: "#F77737" }}>
              {Number(ipoints).toLocaleString()} iP
            </span>
          </div>
        </div>

        {/* Timeline hint */}
        <div className="flex items-center gap-2.5 rounded-2xl px-4 py-3 mb-5 text-start"
          style={{ background: "rgba(247,119,55,0.06)", border: "1px solid rgba(247,119,55,0.15)" }}>
          <Info className="w-4 h-4 flex-shrink-0" style={{ color: "#F77737" }} />
          <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {L(lang, "Typically reviewed within a few minutes.", "Обычно проверяем за несколько минут.", "בדרך כלל נבדק תוך כמה דקות.")}
          </p>
        </div>

        <button onClick={onDone} className="w-full font-bold text-sm flex items-center justify-center gap-2 press-btn"
          style={{ height: 52, borderRadius: 9999, background: "linear-gradient(135deg,#8B5CF6,#4F46E5)", color: "#fff", boxShadow: "0 6px 24px rgba(139,92,246,0.3)", fontSize: 15 }}>
          {L(lang, "Got it", "Понятно", "הבנתי")}
        </button>
      </div>
    </Modal>
  );
}

function ErrorModal({ lang, message, onRetry, onClose }) {
  return (
    <Modal onClose={onClose} accent="#ef4444">
      <div style={{ height: 3, background: "linear-gradient(90deg,#ef4444,#f87171,#ef4444)" }} />
      <div className="px-6 pb-7 pt-5 text-center">
        <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center relative">
          <div className="absolute inset-0 rounded-full" style={{ background: "rgba(239,68,68,0.08)" }} />
          <div className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ background: "linear-gradient(135deg,#ef4444,#dc2626)", boxShadow: "0 8px 32px rgba(239,68,68,0.35)" }}>
            <XCircle className="w-7 h-7 text-white" strokeWidth={2.5} />
          </div>
        </div>

        <h2 className="text-2xl font-black font-outfit mb-1.5" style={{ color: "var(--text-primary)" }}>
          {L(lang, "Payment Failed", "Ошибка оплаты", "התשלום נכשל")}
        </h2>
        <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
          {message || L(lang,
            "Something went wrong. Please check your card and try again.",
            "Что-то пошло не так. Проверьте данные карты и попробуйте снова.",
            "משהו השתבש. בדוק את פרטי הכרטיס ונסה שוב."
          )}
        </p>

        <div className="space-y-2.5">
          <button onClick={onRetry} className="w-full font-bold text-sm flex items-center justify-center gap-2 press-btn"
            style={{ height: 52, borderRadius: 9999, background: "linear-gradient(135deg,#8B5CF6,#4F46E5)", color: "#fff", boxShadow: "0 6px 24px rgba(139,92,246,0.3)", fontSize: 15 }}>
            <RefreshCw className="w-4 h-4" />
            {L(lang, "Try Again", "Попробовать снова", "נסה שוב")}
          </button>
          <button onClick={onClose} className="w-full font-semibold text-sm press-sm"
            style={{ height: 44, borderRadius: 9999, background: "transparent", border: "1px solid var(--border-color)", color: "var(--text-secondary)", fontSize: 14 }}>
            {L(lang, "Cancel", "Отмена", "ביטול")}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Payment method tab selector ───────────────────────────────────────────────

function MethodTabs({ method, onChange, lang }) {
  const tabs = [
    { id: "card", img: CARD_IMG, fit: "cover",   label: L(lang, "Card", "Карта", "כרטיס"), sub: "Visa · Mastercard · Amex", accent: "#8B5CF6", glow: "rgba(139,92,246,0.30)" },
    { id: "bit",  img: BIT_LOGO, fit: "cover",    label: "Bit", sub: L(lang, "Bank transfer", "Перевод", "העברה"), accent: "#E1306C", glow: "rgba(225,48,108,0.30)" },
  ];
  return (
    <div className="flex gap-2 mb-4">
      {tabs.map(tab => {
        const active = method === tab.id;
        return (
          <button key={tab.id} onClick={() => onChange(tab.id)} aria-pressed={active}
            className="flex-1 rounded-2xl press-sm transition-all overflow-hidden"
            style={{
              position: "relative", minHeight: 108, background: "var(--surface-2)",
              border: `2px solid ${active ? tab.accent : "var(--border-color)"}`,
              boxShadow: active ? `0 0 0 1px ${tab.glow}, 0 10px 26px ${tab.glow}` : "none",
            }}>
            {/* real logo / 3D photo filling the whole tile */}
            <div aria-hidden="true" style={{
              position: "absolute", inset: 0,
              backgroundImage: `url(${tab.img})`,
              backgroundSize: tab.fit, backgroundPosition: "center", backgroundRepeat: "no-repeat",
              opacity: active ? 1 : 0.82,
              filter: active ? "none" : "saturate(0.85) brightness(0.9)",
              transform: active ? "scale(1.05)" : "scale(1)",
              transition: "all 320ms ease",
            }} />
            {/* readability scrim for the label */}
            <div aria-hidden="true" style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(to top, rgba(2,2,8,0.82) 0%, rgba(2,2,8,0.32) 42%, rgba(2,2,8,0) 72%)",
            }} />
            {/* label */}
            <div className="px-2 pb-2 text-center" style={{ position: "absolute", insetInline: 0, bottom: 0, zIndex: 1 }}>
              <div className="font-bold text-sm" style={{ color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>{tab.label}</div>
              <div className="text-[10px]" style={{ color: "rgba(255,255,255,0.78)", textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}>{tab.sub}</div>
            </div>
            {/* selected check */}
            {active && (
              <div className="flex items-center justify-center" style={{ position: "absolute", top: 7, insetInlineEnd: 7, zIndex: 1, width: 20, height: 20, borderRadius: 999, background: tab.accent, boxShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>
                <CheckCircle className="w-3.5 h-3.5" style={{ color: "#fff" }} />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

// ── Decorative backdrops: real bit logo / card photo behind the payment content ─
// Official bit logo floating softly behind the Bit payment section
function Bit3DBackdrop() {
  return (
    <div aria-hidden="true" className="select-none pointer-events-none"
      style={{ position: "absolute", inset: 0, overflow: "hidden", zIndex: 0 }}>
      <img src={BIT_LOGO} alt="" style={{
        position: "absolute", top: -26, insetInlineEnd: -22, width: 172, height: 172,
        objectFit: "contain", borderRadius: 38, opacity: 0.16, transform: "rotate(-8deg)",
        filter: "drop-shadow(0 16px 30px rgba(225,48,108,0.45))",
      }} />
    </div>
  );
}

// Realistic 3D card photo floating softly behind the Card payment section
function Card3DBackdrop() {
  return (
    <div aria-hidden="true" className="select-none pointer-events-none"
      style={{ position: "absolute", inset: 0, overflow: "hidden", zIndex: 0 }}>
      <img src={CARD_IMG} alt="" style={{
        position: "absolute", top: -8, insetInlineEnd: -56, width: 268,
        objectFit: "contain", opacity: 0.15,
        filter: "drop-shadow(0 18px 34px rgba(91,33,182,0.5))",
      }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function Test2() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // ── Core state
  const [info, setInfo]                     = useState(null);
  const [loading, setLoading]               = useState(true);
  const [amount, setAmount]                 = useState("");
  const [stage, setStage]                   = useState(1);           // 1=amount, 2=pay, 3=upload(bit)
  const [payMethod, setPayMethod]           = useState("card");       // 'card' | 'bit'

  // ── Card (Allpay) state
  const [cardData, setCardData]             = useState(null);         // {topup_id, payment_url, ipoints_amount}
  const [allpayReady, setAllpayReady]       = useState(false);
  const [paying, setPaying]                 = useState(false);
  const [creatingCard, setCreatingCard]     = useState(false);

  // ── Bit state
  const [bitDraft, setBitDraft]             = useState(null);         // {topup_id, ref_code, amount_ils, ipoints_amount}
  const [uploading, setUploading]           = useState(false);
  const [creatingBit, setCreatingBit]       = useState(false);

  // ── Promo
  const [promoCode, setPromoCode]           = useState("");
  const [promoState, setPromoState]         = useState(null);
  const [promoChecking, setPromoChecking]   = useState(false);
  const [promoOpen, setPromoOpen]           = useState(false);

  // ── UI
  const [displayBalance, setDisplayBalance] = useState(0);
  const [modal, setModal]                   = useState(null);         // null | 'success' | 'pending' | 'error'
  const [modalData, setModalData]           = useState({});

  const allpayRef = useRef(null);
  const pollRef   = useRef(null);

  // ── Return from Allpay redirect
  useEffect(() => {
    const paid = searchParams.get("paid");
    const tid  = searchParams.get("tid");
    if (paid !== "1" || !tid) return;
    window.history.replaceState({}, "", "/test2");
    setStage(2); setPayMethod("card");
    startCardPoll(tid);
    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── postMessage from AllpaySuccess popup
  useEffect(() => {
    const h = (e) => {
      if (e.data?.type === "allpay_success" && e.data.tid) startCardPoll(e.data.tid);
    };
    window.addEventListener("message", h);
    return () => window.removeEventListener("message", h);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load topup info
  useEffect(() => {
    fetch(`${API}/topup/info`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setInfo(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // ── Animated balance
  useEffect(() => {
    const target = user?.ipoints || 0;
    if (!target) { setDisplayBalance(0); return; }
    let current = 0;
    const duration = 900, step = 16;
    const increment = target / (duration / step);
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) { setDisplayBalance(target); clearInterval(timer); }
      else setDisplayBalance(Math.floor(current));
    }, step);
    return () => clearInterval(timer);
  }, [user?.ipoints]);

  // ── Load allpay-hf.js
  useEffect(() => {
    if (document.getElementById("allpay-hf-script")) return;
    const s = document.createElement("script");
    s.id = "allpay-hf-script"; s.src = "https://allpay.to/js/allpay-hf.js"; s.async = true;
    document.body.appendChild(s);
  }, []);

  // ── Init AllpayPayment when iframe ready
  useEffect(() => {
    if (stage !== 2 || payMethod !== "card" || !cardData?.payment_url) return;
    setAllpayReady(false); allpayRef.current = null;
    let tries = 0;
    const init = () => {
      tries++;
      if (tries > 40) return;
      if (!window.AllpayPayment) { setTimeout(init, 250); return; }
      try {
        allpayRef.current = new window.AllpayPayment({
          iframeId: IFRAME_ID,
          onSuccess: () => { setPaying(false); if (cardData?.topup_id) startCardPoll(cardData.topup_id); },
          onError: (code, msg) => {
            setPaying(false);
            console.error("AllpayPayment onError:", code, msg);
            const display = [msg, code].filter(Boolean).join(" | ") || L(lang, "Card payment failed. Please try again.", "Ошибка карты. Попробуйте снова.", "תשלום הכרטיס נכשל. נסה שוב.");
            setModal("error");
            setModalData({ message: display });
          },
        });
        setAllpayReady(true);
      } catch (e) { console.error("AllpayPayment init:", e); }
    };
    setTimeout(init, 900);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, payMethod, cardData?.payment_url]);

  // ── Calculations
  const rate               = info?.exchange_rate || 1;
  const minTopupIls        = Number(info?.min_topup_ils || MIN_TOPUP_ILS);
  const numericAmount      = parseFloat(amount || "0");
  const hasEntered         = Number.isFinite(numericAmount) && numericAmount > 0;
  const hasValid           = Number.isFinite(numericAmount) && numericAmount >= minTopupIls;
  const isBelowMin         = hasEntered && numericAmount < minTopupIls;
  const baseIp             = hasEntered ? Math.floor(numericAmount * rate) : 0;
  const bonusEvent         = info?.bonus_event;
  const globalPct          = bonusEvent?.bonus_pct || 0;
  const globalMin          = bonusEvent?.min_topup_ils || 0;
  const globalElig         = hasValid && bonusEvent && (globalMin === 0 || numericAmount >= globalMin);
  const globalBonus        = globalElig ? Math.floor(baseIp * globalPct / 100) : 0;
  const largePct           = info?.large_topup_bonus_pct || 0;
  const largeThresh        = info?.large_topup_bonus_threshold_ils || 200;
  const largeElig          = hasValid && numericAmount >= largeThresh && largePct > 0;
  const largeBonus         = largeElig ? Math.floor(baseIp * largePct / 100) : 0;
  const promoValid         = promoState?.status === "valid";
  const promoMinIls        = promoValid ? (Number(promoState.min_topup_ils) || 0) : 0;
  const promoBelowMin      = promoValid && hasValid && numericAmount < promoMinIls;
  const promoBonus         = promoValid && hasValid && !promoBelowMin
    ? (promoState.bonus_type === "fixed" ? promoState.bonus_value : Math.floor(baseIp * promoState.bonus_value / 100))
    : 0;
  const totalIp            = baseIp + globalBonus + largeBonus + promoBonus;
  const hasAnyBonus        = hasValid && (globalBonus > 0 || largeBonus > 0 || promoBonus > 0);
  const displayIp          = modalData?.ipoints || (cardData ? cardData.ipoints_amount : bitDraft ? bitDraft.ipoints_amount : totalIp);

  // ── Helpers
  const startCardPoll = useCallback((tid) => {
    clearInterval(pollRef.current);
    let tries = 0;
    const poll = async () => {
      tries++;
      try {
        const r = await fetch(`${API}/allpay/status/${tid}`, { credentials: "include" });
        if (!r.ok) return;
        const d = await r.json();
        if (d.approved) {
          clearInterval(pollRef.current);
          setModal("success");
          setModalData({ ipoints: d.ipoints_amount, amountIls: d.amount_ils });
        } else if (tries >= 20) {
          clearInterval(pollRef.current);
          setModal("success");
          setModalData({ ipoints: d.ipoints_amount || 0, amountIls: d.amount_ils || 0 });
        }
      } catch {}
    };
    poll();
    pollRef.current = setInterval(poll, 2000);
  }, []);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success(t("dash.copy", lang)); }
    catch { toast.error(t("TopUp.could_not_copy_try_manually", lang)); }
  };

  const checkPromo = useCallback(async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    setPromoChecking(true);
    try {
      const r = await fetch(`${API}/promo/preview/${code}`, { credentials: "include" });
      const d = await r.json();
      setPromoState(d);
      if (d.status === "valid") toast.success(t("TopUp.promo_code_applied", lang));
      else if (d.status === "expired") toast.error(t("TopUp.code_expired", lang));
      else if (d.status === "already_used") toast.error(t("TopUp.already_used", lang));
      else toast.error(t("TopUp.invalid_code", lang));
    } catch { toast.error(t("TopUp.connection_error_try_again", lang)); }
    finally { setPromoChecking(false); }
  }, [promoCode, lang]);

  // ── Handlers
  const handleContinue = async () => {
    if (!hasValid) return;
    if (payMethod === "card") {
      if (creatingCard) return;
      setCreatingCard(true);
      try {
        const r = await fetch(`${API}/allpay/create`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ amount_ils: numericAmount, promo_code: promoCode.trim().toUpperCase() || undefined }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || "Error");
        setCardData(d); setStage(2);
      } catch (e) {
        setModal("error"); setModalData({ message: e.message });
      } finally { setCreatingCard(false); }
    } else {
      if (creatingBit) return;
      setCreatingBit(true);
      try {
        const r = await fetch(`${API}/topup/create`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ amount_ils: numericAmount, promo_code: promoCode.trim().toUpperCase() || undefined }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail);
        setBitDraft(d); setStage(2);
      } catch (e) {
        setModal("error"); setModalData({ message: e.message });
      } finally { setCreatingBit(false); }
    }
  };

  const handleCardPay = () => {
    if (!allpayRef.current || paying || !allpayReady) return;
    setPaying(true);
    allpayRef.current.pay();
  };

  const handleBitUpload = async (file) => {
    if (!bitDraft?.topup_id || !file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await fetch(`${API}/upload`, { method: "POST", body: fd, credentials: "include" });
      if (!up.ok) throw new Error(t("TopUp.upload_failed_please_try_again", lang));
      const { url } = await up.json();
      const r = await fetch(`${API}/topup/submit-proof`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ topup_id: bitDraft.topup_id, proof_image: url }),
      });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail);
      setModal("pending");
      setModalData({ refCode: bitDraft.ref_code, ipoints: bitDraft.ipoints_amount });
    } catch (e) { toast.error(e.message || t("DashboardV2.something_went_wrong_try_again", lang)); }
    finally { setUploading(false); }
  };

  const resetToStart = () => {
    setStage(1); setCardData(null); setBitDraft(null);
    setAllpayReady(false); allpayRef.current = null;
    clearInterval(pollRef.current); setPaying(false);
    setModal(null); setModalData({});
  };

  const handleMethodSwitch = (m) => {
    if (m === payMethod) return;
    // Cancel existing sessions when switching
    if (bitDraft?.topup_id) {
      fetch(`${API}/topup/cancel-draft`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topup_id: bitDraft.topup_id }),
      }).catch(() => {});
    }
    setPayMethod(m);
    setCardData(null); setBitDraft(null);
    setAllpayReady(false); allpayRef.current = null;
    setStage(1);
  };

  // ── Shared components
  const StepDot = ({ n, active, done }) => (
    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0"
      style={{
        background: done ? "#22c55e" : active ? "#8B5CF6" : "var(--surface-3)",
        color: (done || active) ? "#fff" : "var(--text-secondary)",
        border: `2px solid ${done ? "#22c55e" : active ? "#8B5CF6" : "var(--border-color)"}`,
        minWidth: 28,
      }}>
      {done ? <CheckCircle className="w-3.5 h-3.5" /> : n}
    </div>
  );

  const cardStyle = (active) => ({
    background: "var(--surface-1)",
    border: `1.5px solid ${active ? "rgba(139,92,246,0.4)" : "var(--border-color)"}`,
    borderRadius: 16,
    boxShadow: active ? "0 4px 24px rgba(139,92,246,0.1)" : "none",
    transition: "border-color 250ms, box-shadow 250ms",
    marginBottom: 12, overflow: "hidden",
  });

  const creatingPayment = payMethod === "card" ? creatingCard : creatingBit;
  const totalSteps = payMethod === "bit" ? 3 : 2;
  const stepLabels = payMethod === "bit"
    ? [L(lang, "Amount", "Сумма", "סכום"), L(lang, "Payment", "Оплата", "תשלום"), L(lang, "Proof", "Чек", "קבלה")]
    : [L(lang, "Amount", "Сумма", "סכום"), L(lang, "Card", "Карта", "כרטיס")];

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin h-5 w-5 border-2 border-[#8B5CF6] border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="min-h-screen pt-14 pb-28" style={{ background: "var(--bg-primary)", overflowX: "hidden" }}>

      {/* Header */}
      <div style={{ borderBottom: "1px solid var(--border-color)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5">
          <h1 className="text-2xl font-black font-outfit" style={{ color: "var(--text-primary)" }}>
            {t("TopUp.top_up", lang)}
          </h1>
          <p className="text-sm mt-0.5" style={{ color: "var(--text-secondary)" }}>
            {L(lang, "Add iPoints to your account", "Пополните счёт iPoints", "הוסף iPoints לחשבון שלך")}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-5 lg:flex lg:gap-8 lg:items-start">
        <div className="flex-1 min-w-0">

          {/* Step progress */}
          <div className="flex items-center mb-5">
            {stepLabels.map((label, i) => {
              const n = i + 1, done = stage > n, active = stage === n;
              return (
                <div key={n} className="flex items-center min-w-0"
                  style={{ flex: i < totalSteps - 1 ? "1 1 0" : "0 0 auto" }}>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <StepDot n={n} active={active} done={done} />
                    <span className="text-xs font-semibold hidden sm:block"
                      style={{ color: active ? "var(--text-primary)" : "var(--text-secondary)" }}>
                      {label}
                    </span>
                  </div>
                  {i < totalSteps - 1 && (
                    <div className="flex-1 rounded-full mx-2 sm:mx-3"
                      style={{ height: 2, minWidth: 12, background: done ? "#22c55e" : "var(--border-color)", transition: "background 400ms" }} />
                  )}
                </div>
              );
            })}
          </div>

          {/* ══ STEP 1: Amount + method ══ */}
          <div style={cardStyle(stage === 1)}>
            <div className="flex items-center justify-between px-4 py-3"
              style={{ borderBottom: "1px solid var(--border-color)" }}>
              <div className="flex items-center gap-2.5 min-w-0">
                <StepDot n={1} active={stage === 1} done={stage > 1} />
                <span className="font-bold text-sm truncate" style={{ color: "var(--text-primary)" }}>
                  {t("TopUp.choose_amount", lang)}
                </span>
              </div>
              {stage > 1 && (
                <button onClick={resetToStart}
                  className="flex items-center gap-1.5 text-xs px-3 h-7 rounded-lg font-semibold flex-shrink-0 ms-3 press-sm"
                  style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>
                  <ArrowLeft className="w-3 h-3 rtl-flip" />{t("TopUp.edit", lang)}
                </button>
              )}
            </div>

            {stage === 1 ? (
              <div className="p-5 space-y-4">
                {/* Quick amounts */}
                <div className="grid grid-cols-3 gap-2">
                  {QUICK_AMOUNTS.map(q => {
                    const qBase = Math.floor(q * rate);
                    const qLarge = q >= largeThresh && largePct > 0 ? Math.floor(qBase * largePct / 100) : 0;
                    const qGlobal = bonusEvent && (globalMin === 0 || q >= globalMin) ? Math.floor(qBase * globalPct / 100) : 0;
                    const qTotal = qBase + qLarge + qGlobal;
                    const qBonusPct = (qLarge > 0 ? largePct : 0) + (qGlobal > 0 ? globalPct : 0);
                    const isSelected = amount === String(q);
                    const hasBonus = qLarge > 0 || qGlobal > 0;
                    const isHot = q === 100;
                    return (
                      <button key={q} onClick={() => setAmount(String(q))} className="press-sm" style={{
                        position: "relative", display: "flex", flexDirection: "column",
                        alignItems: "center", justifyContent: "center", gap: 4,
                        padding: "14px 4px 10px", borderRadius: 14,
                        border: `1.5px solid ${isSelected ? "#8B5CF6" : hasBonus ? "rgba(34,197,94,0.3)" : "var(--border-color)"}`,
                        background: isSelected ? "linear-gradient(160deg,#8B5CF6,#5B21B6)" : "var(--surface-2)",
                        boxShadow: isSelected ? "0 2px 14px rgba(139,92,246,0.28)" : "none",
                        cursor: "pointer", minHeight: 76, overflow: "hidden",
                      }}>
                        {(isHot || hasBonus) && !isSelected && (
                          <span style={{
                            position: "absolute", top: 0, right: 0, fontSize: 9, fontWeight: 800,
                            lineHeight: 1.4, padding: "2px 6px", borderRadius: "0 14px 0 7px", whiteSpace: "nowrap",
                            ...(isHot ? { background: "#F77737", color: "#fff" } : { background: "rgba(34,197,94,0.85)", color: "#fff" }),
                          }}>{isHot ? "🔥" : `+${qBonusPct}%`}</span>
                        )}
                        <span style={{ fontFamily: "Outfit, sans-serif", fontWeight: 900, fontSize: "clamp(0.85rem, 3.8vw, 1.1rem)", lineHeight: 1.1, color: isSelected ? "#fff" : "#F77737", whiteSpace: "nowrap" }}>
                          {qTotal.toLocaleString()}<span style={{ fontSize: "0.6em", fontWeight: 700, marginInlineStart: 2, opacity: isSelected ? 0.7 : 0.5 }}>iP</span>
                        </span>
                        <span style={{ fontFamily: "Outfit, sans-serif", fontWeight: 600, fontSize: "clamp(0.7rem, 2.8vw, 0.85rem)", color: isSelected ? "rgba(255,255,255,0.65)" : "var(--text-secondary)" }}>₪{q}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Custom amount */}
                <div className="relative">
                  <span className="absolute start-4 top-1/2 -translate-y-1/2 text-xl font-black font-outfit pointer-events-none select-none"
                    style={{ color: "var(--text-secondary)" }}>₪</span>
                  <input type="number" inputMode="decimal" min={minTopupIls} step="0.01"
                    value={amount} onChange={e => setAmount(e.target.value)}
                    placeholder={String(minTopupIls)}
                    className="w-full rounded-2xl font-black font-outfit outline-none ps-10 pe-4"
                    style={{ height: 56, fontSize: "clamp(1.2rem, 5vw, 1.6rem)", background: "var(--surface-2)", border: `2px solid ${isBelowMin ? "rgba(239,68,68,0.5)" : hasValid ? "rgba(139,92,246,0.5)" : "var(--border-color)"}`, color: "var(--text-primary)", transition: "border-color 200ms" }}
                  />
                </div>

                {hasEntered && !hasAnyBonus && (
                  <div className="flex items-baseline justify-between px-1">
                    <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                      {L(lang, "You receive", "Вы получите", "תקבל")}
                    </span>
                    <span className="text-lg font-black font-outfit" style={{ color: "#F77737" }}>
                      {totalIp.toLocaleString()} <span className="text-sm opacity-60">iP</span>
                    </span>
                  </div>
                )}

                {isBelowMin && (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
                    style={{ background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.2)" }}>
                    <XCircle className="w-4 h-4 flex-shrink-0" style={{ color: "#ef4444" }} />
                    <p className="text-xs" style={{ color: "#ef4444" }}>
                      {L(lang, `Minimum is ₪${minTopupIls}`, `Минимум — ₪${minTopupIls}`, `מינימום ₪${minTopupIls}`)}
                    </p>
                  </div>
                )}

                {hasValid && hasAnyBonus && (
                  <div className="rounded-xl px-4 py-3 space-y-2"
                    style={{ border: "1px solid rgba(34,197,94,0.2)", background: "rgba(34,197,94,0.04)" }}>
                    <div className="flex justify-between text-sm">
                      <span style={{ color: "var(--text-secondary)" }}>{t("TopUp.base_amount", lang)}</span>
                      <span className="font-semibold font-outfit" style={{ color: "var(--text-primary)" }}>{baseIp.toLocaleString()} iP</span>
                    </div>
                    {largeElig && <div className="flex justify-between text-sm">
                      <span style={{ color: "#22c55e" }}>{L(lang, `Bonus +${largePct}%`, `Бонус +${largePct}%`, `בונוס +${largePct}%`)}</span>
                      <span className="font-bold font-outfit" style={{ color: "#22c55e" }}>+{largeBonus.toLocaleString()} iP</span>
                    </div>}
                    {globalElig && globalBonus > 0 && <div className="flex justify-between text-sm">
                      <span style={{ color: "#a78bfa" }}>🎉 +{globalPct}%</span>
                      <span className="font-bold font-outfit" style={{ color: "#a78bfa" }}>+{globalBonus.toLocaleString()} iP</span>
                    </div>}
                    {promoBonus > 0 && <div className="flex justify-between text-sm">
                      <span style={{ color: "#22c55e" }}>🎟 Promo</span>
                      <span className="font-bold font-outfit" style={{ color: "#22c55e" }}>+{promoBonus.toLocaleString()} iP</span>
                    </div>}
                    <div style={{ height: 1, background: "rgba(34,197,94,0.12)" }} />
                    <div className="flex justify-between">
                      <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>{t("Shop.total", lang)}</span>
                      <span className="text-sm font-black font-outfit" style={{ color: "#F77737" }}>{totalIp.toLocaleString()} iP</span>
                    </div>
                  </div>
                )}

                {largePct > 0 && !largeElig && hasValid && (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
                    style={{ background: "rgba(34,197,94,0.05)", border: "1px solid rgba(34,197,94,0.15)" }}>
                    <Zap className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#22c55e" }} />
                    <p className="text-xs" style={{ color: "#22c55e" }}>
                      {L(lang, `Add ₪${(largeThresh - numericAmount).toFixed(0)} more → +${largePct}% bonus`, `Добавьте ₪${(largeThresh - numericAmount).toFixed(0)} → +${largePct}%`, `הוסף ₪${(largeThresh - numericAmount).toFixed(0)} → +${largePct}% בונוס`)}
                    </p>
                  </div>
                )}

                {!promoOpen ? (
                  <button onClick={() => setPromoOpen(true)} className="text-xs font-semibold press-sm"
                    style={{ color: "var(--text-secondary)" }}>
                    + {L(lang, "I have a promo code", "Есть промокод", "יש לי קוד קופון")}
                  </button>
                ) : (
                  <div className="space-y-1.5">
                    <div className="flex gap-2">
                      <input value={promoCode}
                        onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoState(null); }}
                        onKeyDown={e => { if (e.key === "Enter") checkPromo(); }}
                        placeholder={t("TopUp.promo_code_optional", lang)}
                        className="flex-1 h-11 rounded-xl text-sm px-3 outline-none font-medium tracking-wider min-w-0"
                        style={{ background: "var(--surface-2)", border: `1.5px solid ${promoValid ? "rgba(34,197,94,0.5)" : promoState && promoState.status !== "valid" ? "rgba(239,68,68,0.4)" : "var(--border-color)"}`, color: "var(--text-primary)" }}
                        maxLength={20} autoFocus />
                      <button onClick={checkPromo} disabled={!promoCode.trim() || promoChecking}
                        className="h-11 px-4 rounded-xl text-sm font-semibold press-sm flex-shrink-0"
                        style={{ background: promoValid ? "rgba(34,197,94,0.15)" : "#8B5CF6", color: promoValid ? "#22c55e" : "#fff", opacity: !promoCode.trim() ? 0.4 : 1 }}>
                        {promoChecking ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : promoValid ? "✓" : t("dash.apply", lang)}
                      </button>
                    </div>
                    {promoState && promoState.status !== "valid" && (
                      <p className="text-xs ps-1" style={{ color: "#ef4444" }}>
                        {promoState.status === "expired" ? t("TopUp.code_expired", lang) : promoState.status === "already_used" ? t("TopUp.already_used", lang) : t("TopUp.invalid_code", lang)}
                      </p>
                    )}
                    {promoBelowMin && (
                      <p className="text-xs ps-1" style={{ color: "#f59e0b" }}>
                        {L(lang, `Min. ₪${promoMinIls} for this code`, `Мин. ₪${promoMinIls}`, `מינימום ₪${promoMinIls} לקוד זה`)}
                      </p>
                    )}
                  </div>
                )}

                {/* Payment method selector */}
                <div>
                  <p className="text-xs font-semibold mb-2" style={{ color: "var(--text-secondary)" }}>
                    {L(lang, "Payment method", "Способ оплаты", "שיטת תשלום")}
                  </p>
                  <MethodTabs method={payMethod} onChange={handleMethodSwitch} lang={lang} />
                </div>

                <button onClick={handleContinue} disabled={!hasValid || creatingPayment}
                  className="w-full font-bold text-base flex items-center justify-center gap-2.5 press-btn"
                  style={{ height: 54, borderRadius: 9999, background: hasValid ? (payMethod === "bit" ? "#E1306C" : "linear-gradient(135deg,#8B5CF6,#4F46E5)") : "var(--surface-2)", color: hasValid ? "#fff" : "var(--text-secondary)", opacity: creatingPayment ? 0.7 : 1, cursor: hasValid && !creatingPayment ? "pointer" : "not-allowed", boxShadow: hasValid ? `0 4px 20px ${payMethod === "bit" ? "rgba(225,48,108,0.3)" : "rgba(139,92,246,0.3)"}` : "none", transition: "all 200ms" }}>
                  {creatingPayment
                    ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{L(lang, "Loading...", "Загрузка...", "טוען...")}</>
                    : payMethod === "bit"
                      ? <>{L(lang, `Continue with Bit — ₪${numericAmount || 0}`, `Продолжить через Bit — ₪${numericAmount || 0}`, `המשך עם Bit — ₪${numericAmount || 0}`)}<ChevronRight className="w-4 h-4 rtl-flip" /></>
                      : <><CreditCard className="w-4 h-4" />{L(lang, `Pay with Card — ₪${numericAmount || 0}`, `Оплатить картой — ₪${numericAmount || 0}`, `שלם בכרטיס — ₪${numericAmount || 0}`)}</>
                  }
                </button>
              </div>
            ) : (
              <div className="px-4 py-3 flex items-center gap-2.5 flex-wrap">
                <span className="font-black font-outfit text-base" style={{ color: "var(--text-primary)" }}>
                  ₪{fmtIls(numericAmount)}
                </span>
                <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 rtl-flip" style={{ color: "var(--text-secondary)", opacity: 0.4 }} />
                <span className="font-black font-outfit text-base" style={{ color: "#F77737" }}>
                  {displayIp.toLocaleString()} iP
                </span>
                <span className="ms-auto text-xs px-2.5 py-1 rounded-lg font-semibold flex-shrink-0"
                  style={{ background: payMethod === "bit" ? "rgba(225,48,108,0.1)" : "rgba(139,92,246,0.1)", color: payMethod === "bit" ? "#E1306C" : "#8B5CF6" }}>
                  {payMethod === "bit" ? "Bit" : L(lang, "Card", "Карта", "כרטיס")}
                </span>
              </div>
            )}
          </div>

          {/* ══ STEP 2: Payment form ══ */}
          <div style={{ ...cardStyle(stage === 2), opacity: stage >= 2 ? 1 : 0.5, pointerEvents: stage >= 2 ? "auto" : "none" }}>
            <div className="px-4 py-3 flex items-center gap-2.5" style={{ borderBottom: "1px solid var(--border-color)" }}>
              <StepDot n={2} active={stage === 2} done={stage > 2} />
              <span className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>
                {payMethod === "bit" ? L(lang, "Pay with Bit", "Оплата Bit", "תשלום עם Bit") : L(lang, "Card Payment", "Оплата картой", "תשלום בכרטיס")}
              </span>
              {stage === 2 && payMethod === "card" && (
                <div className="ms-auto flex items-center gap-1 text-xs" style={{ color: "#22c55e" }}>
                  <ShieldCheck className="w-3.5 h-3.5" />
                  {L(lang, "Secure", "Защищено", "מאובטח")}
                </div>
              )}
            </div>

            {stage === 2 ? (
              payMethod === "card" && cardData?.payment_url ? (
                <div className="p-4" style={{ position: "relative", overflow: "hidden" }}>
                  <Card3DBackdrop />
                  <div className="space-y-4" style={{ position: "relative", zIndex: 1 }}>

                  {/* Light-theme card — iframe has transparent body, needs white bg */}
                  <div style={{
                    borderRadius: 20,
                    overflow: "hidden",
                    background: "#ffffff",
                    boxShadow: "0 0 0 1.5px rgba(139,92,246,0.22), 0 8px 40px rgba(0,0,0,0.22)",
                    padding: "20px 16px 16px",
                  }}>
                    {/* card header label */}
                    <div className="flex items-center justify-between mb-3 px-1">
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#374151", letterSpacing: "0.03em" }}>
                        {L(lang, "Card Details", "Данные карты", "פרטי כרטיס")}
                      </span>
                      <div className="flex items-center gap-1.5" style={{ color: "#6b7280" }}>
                        <Lock style={{ width: 11, height: 11 }} />
                        <span style={{ fontSize: 11, fontWeight: 600 }}>Secure</span>
                      </div>
                    </div>

                    {/* auto-height iframe: allpay-hf.js sets height via postMessage */}
                    <iframe id={IFRAME_ID} src={cardData.payment_url}
                      allow="payment *" referrerPolicy="no-referrer"
                      style={{ width: "100%", minHeight: 220, border: "none", display: "block", borderRadius: 12 }}
                      title="Card Payment" />
                  </div>

                  {/* Pay button */}
                  <button onClick={handleCardPay} disabled={!allpayReady || paying}
                    className="w-full font-black flex items-center justify-center gap-2.5 press-btn"
                    style={{
                      height: 56, borderRadius: 9999, fontSize: 16,
                      background: allpayReady && !paying
                        ? "linear-gradient(135deg,#8B5CF6 0%,#6d28d9 50%,#4F46E5 100%)"
                        : "rgba(255,255,255,0.06)",
                      color: allpayReady && !paying ? "#fff" : "var(--text-secondary)",
                      opacity: paying ? 0.75 : 1,
                      boxShadow: allpayReady && !paying ? "0 6px 28px rgba(139,92,246,0.38), 0 2px 0 rgba(255,255,255,0.08) inset" : "none",
                      transition: "all 220ms", letterSpacing: "0.01em",
                    }}>
                    {paying ? (
                      <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{L(lang, "Processing...", "Обработка...", "מעבד...")}</>
                    ) : !allpayReady ? (
                      <><div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />{L(lang, "Loading...", "Загружаем...", "טוען...")}</>
                    ) : (
                      <><Lock className="w-4 h-4" style={{ opacity: 0.85 }} />{L(lang, `Pay ₪${fmtIls(numericAmount)}`, `Оплатить ₪${fmtIls(numericAmount)}`, `שלם ₪${fmtIls(numericAmount)}`)}</>
                    )}
                  </button>

                  {/* Footer */}
                  <div className="flex items-center justify-center gap-3">
                    {[
                      { icon: <ShieldCheck style={{ width: 11, height: 11 }} />, label: "PCI DSS" },
                      { icon: <Lock style={{ width: 11, height: 11 }} />, label: "SSL" },
                      { icon: <span style={{ fontSize: 9, fontWeight: 900 }}>3DS</span>, label: "3D Secure" },
                    ].map(({ icon, label }) => (
                      <div key={label} className="flex items-center gap-1" style={{ color: "var(--text-secondary)", opacity: 0.4 }}>
                        {icon}
                        <span style={{ fontSize: 10, fontWeight: 600 }}>{label}</span>
                      </div>
                    ))}
                  </div>
                  </div>
                </div>

              ) : payMethod === "bit" && bitDraft ? (
                <div className="p-5" style={{ position: "relative", overflow: "hidden" }}>
                  <Bit3DBackdrop />
                  <div className="space-y-4" style={{ position: "relative", zIndex: 1 }}>
                  {/* Code box */}
                  <div className="rounded-2xl text-center px-4 py-5"
                    style={{ background: "var(--surface-2)", border: "2px dashed rgba(225,48,108,0.25)" }}>
                    <div className="flex items-center justify-center gap-1.5 mb-2">
                      <Hash className="w-3.5 h-3.5" style={{ color: "#E1306C" }} />
                      <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#E1306C" }}>
                        {t("TopUp.payment_code", lang)}
                      </span>
                    </div>
                    <div className="font-black font-outfit mb-3"
                      style={{ fontSize: "clamp(1.5rem, 8vw, 2.75rem)", letterSpacing: "0.18em", color: "var(--text-primary)", wordBreak: "break-all" }}>
                      {bitDraft.ref_code}
                    </div>
                    <button onClick={() => copy(bitDraft.ref_code)}
                      className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-xl press-sm"
                      style={{ background: "rgba(225,48,108,0.1)", border: "1px solid rgba(225,48,108,0.25)", color: "#E1306C" }}>
                      <Copy className="w-3.5 h-3.5" />
                      {t("TopUp.copy_code", lang)}
                    </button>
                  </div>

                  {/* Instructions */}
                  <div className="space-y-3">
                    {[
                      L(lang, `Open Bit and send ₪${fmtIls(numericAmount)} to the iFollowers account`, `Откройте Bit, отправьте ₪${fmtIls(numericAmount)} на счёт iFollowers`, `פתח Bit ושלח ₪${fmtIls(numericAmount)} לחשבון iFollowers`),
                      t("TopUp.paste_the_code_above_in_the_payment_reason_field", lang),
                      t("TopUp.complete_the_payment_and_come_back_here", lang),
                    ].map((text, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0 mt-0.5"
                          style={{ background: "#E1306C", color: "#fff", minWidth: 28 }}>{i + 1}</div>
                        <p className="text-sm leading-relaxed pt-0.5" style={{ color: "var(--text-secondary)" }}>{text}</p>
                      </div>
                    ))}
                  </div>

                  {/* QR */}
                  <div className="hidden sm:flex flex-col items-center gap-3 py-2">
                    <div className="relative">
                      <div className="rounded-2xl p-3" style={{ background: "#fff" }}>
                        <QRCodeSVG value={BITPAY_URL} size={120} level="H" />
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black"
                          style={{ background: "#E1306C", color: "#fff" }}>Bit</div>
                      </div>
                    </div>
                    <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
                      {t("TopUp.scan_with_bit_app", lang)}
                    </p>
                  </div>

                  {/* Bit action buttons */}
                  <div className="space-y-2.5">
                    <button onClick={() => window.open(BITPAY_URL, "_blank", "noopener,noreferrer")}
                      className="w-full font-bold text-sm flex items-center justify-center gap-2.5 press-btn"
                      style={{ height: 52, borderRadius: 9999, background: "#E1306C", color: "#fff", boxShadow: "0 4px 16px rgba(225,48,108,0.35)" }}>
                      <span className="font-black text-base">bit</span>
                      {L(lang, "Open Bit App", "Открыть Bit", "פתח את Bit")}
                    </button>
                    <button onClick={() => setStage(3)}
                      className="w-full font-semibold text-sm flex items-center justify-center gap-2 press-btn"
                      style={{ height: 46, borderRadius: 9999, background: "rgba(34,197,94,0.08)", border: "1.5px solid rgba(34,197,94,0.25)", color: "#22c55e" }}>
                      <CheckCircle className="w-4 h-4" />
                      {t("TopUp.i_paid_upload_proof", lang)}
                    </button>
                  </div>
                  </div>
                </div>

              ) : (
                <div className="p-8 text-center">
                  <div className="animate-spin h-6 w-6 border-2 border-[#8B5CF6] border-t-transparent rounded-full mx-auto mb-3" />
                  <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                    {L(lang, "Confirming payment...", "Подтверждение...", "מאשר תשלום...")}
                  </p>
                </div>
              )
            ) : (
              <div className="px-4 py-6 text-center">
                <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                  {t("TopUp.choose_an_amount_first", lang)}
                </p>
              </div>
            )}
          </div>

          {/* ══ STEP 3: Upload proof (Bit only) ══ */}
          {payMethod === "bit" && (
            <div style={{ ...cardStyle(stage === 3), opacity: stage >= 3 ? 1 : 0.5, pointerEvents: stage >= 3 ? "auto" : "none" }}>
              <div className="px-4 py-3 flex items-center justify-between"
                style={{ borderBottom: "1px solid var(--border-color)" }}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <StepDot n={3} active={stage === 3} done={stage > 3} />
                  <span className="font-bold text-sm truncate" style={{ color: "var(--text-primary)" }}>
                    {t("TopUp.upload_proof", lang)}
                  </span>
                </div>
                {stage >= 3 && displayIp > 0 && (
                  <div className="text-end flex-shrink-0 ms-3">
                    <div className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: "var(--text-secondary)" }}>
                      {t("TopUp.expected", lang)}
                    </div>
                    <div className="text-sm font-black font-outfit" style={{ color: "#F77737" }}>
                      {Number(displayIp).toLocaleString()} iP
                    </div>
                  </div>
                )}
              </div>

              {stage >= 3 ? (
                <div className="p-5">
                  <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
                    {t("TopUp.upload_a_screenshot_of_your_completed_bit_paymen", lang)}
                  </p>
                  <label className={`block rounded-2xl border-2 border-dashed text-center cursor-pointer ${uploading ? "opacity-60" : ""}`}
                    style={{ borderColor: uploading ? "#8B5CF6" : "rgba(139,92,246,0.25)", padding: "40px 20px", background: "rgba(139,92,246,0.03)", transition: "border-color 200ms" }}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleBitUpload(f); }}>
                    <input type="file" className="hidden" accept="image/*"
                      onChange={e => e.target.files[0] && handleBitUpload(e.target.files[0])}
                      disabled={uploading} />
                    {uploading ? (
                      <div className="flex flex-col items-center gap-3">
                        <div className="animate-spin h-8 w-8 border-2 border-[#8B5CF6] border-t-transparent rounded-full" />
                        <span className="text-sm font-semibold" style={{ color: "var(--text-secondary)" }}>
                          {t("DashboardV2.uploading", lang)}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
                          style={{ background: "rgba(139,92,246,0.08)", border: "1.5px solid rgba(139,92,246,0.2)" }}>
                          <ImagePlus className="w-7 h-7" style={{ color: "#a78bfa" }} />
                        </div>
                        <div>
                          <div className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
                            {t("TopUp.upload_screenshot", lang)}
                          </div>
                          <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
                            {t("TopUp.click_or_drag_drop", lang)}
                          </div>
                        </div>
                      </div>
                    )}
                  </label>
                </div>
              ) : (
                <div className="px-4 py-6 text-center">
                  <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                    {t("TopUp.complete_payment_first", lang)}
                  </p>
                </div>
              )}
            </div>
          )}

        </div>

        {/* ── Right sidebar ── */}
        <div className="hidden lg:block flex-shrink-0" style={{ width: 268 }}>
          <div style={{ position: "sticky", top: 80 }}>
            {user && (
              <div className="rounded-2xl p-4 mb-3" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
                <div className="text-[10px] uppercase tracking-widest font-semibold mb-3" style={{ color: "var(--text-secondary)" }}>
                  {t("cart.your_balance", lang)}
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(247,119,55,0.1)", border: "1px solid rgba(247,119,55,0.2)" }}>
                    <Coins className="w-5 h-5" style={{ color: "#F77737" }} />
                  </div>
                  <div className="flex items-baseline gap-1.5 min-w-0">
                    <span className="text-2xl font-black font-outfit truncate" style={{ color: "#F77737" }}>{fmtIP(displayBalance)}</span>
                    <span className="text-sm font-bold flex-shrink-0" style={{ color: "rgba(247,119,55,0.5)" }}>iP</span>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2 px-3 rounded-xl" style={{ background: "var(--surface-2)" }}>
                  <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{L(lang, "Rate", "Курс", "קורס")}</span>
                  <span className="text-sm font-bold font-outfit" style={{ color: "var(--text-primary)" }}>₪1 = <span style={{ color: "#F77737" }}>{rate} iP</span></span>
                </div>
              </div>
            )}

            {hasEntered && (
              <div className="rounded-2xl mb-3" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)", overflow: "hidden" }}>
                <div className="px-4 py-2.5 flex items-center gap-1.5" style={{ borderBottom: "1px solid var(--border-color)" }}>
                  <Sparkles className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#8B5CF6" }} />
                  <span className="text-xs font-semibold" style={{ color: "var(--text-secondary)" }}>{t("TopUp.you_get", lang)}</span>
                </div>
                <div className="px-4 py-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span style={{ color: "var(--text-secondary)" }}>{t("TopUp.base_amount", lang)}</span>
                    <span className="font-semibold font-outfit" style={{ color: "var(--text-primary)" }}>{baseIp.toLocaleString()} iP</span>
                  </div>
                  {largeElig && <div className="flex justify-between text-sm">
                    <span style={{ color: "#22c55e" }}>+{largePct}%</span>
                    <span className="font-bold font-outfit" style={{ color: "#22c55e" }}>+{largeBonus.toLocaleString()} iP</span>
                  </div>}
                  {globalElig && globalBonus > 0 && <div className="flex justify-between text-sm">
                    <span style={{ color: "#a78bfa" }}>🎉 +{globalPct}%</span>
                    <span className="font-bold font-outfit" style={{ color: "#a78bfa" }}>+{globalBonus.toLocaleString()} iP</span>
                  </div>}
                  {promoBonus > 0 && <div className="flex justify-between text-sm">
                    <span style={{ color: "#22c55e" }}>🎟 Promo</span>
                    <span className="font-bold font-outfit" style={{ color: "#22c55e" }}>+{promoBonus.toLocaleString()} iP</span>
                  </div>}
                  <div style={{ height: 1, background: "var(--border-color)" }} />
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>{t("Shop.total", lang)}</span>
                    <span className="text-lg font-black font-outfit" style={{ color: "#F77737" }}>{totalIp.toLocaleString()} iP</span>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-2xl p-4 space-y-3" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
              <div className="flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--text-secondary)" }} />
                <span className="text-xs font-semibold" style={{ color: "var(--text-secondary)" }}>{L(lang, "How it works", "Как это работает", "איך זה עובד")}</span>
              </div>
              {(payMethod === "card"
                ? [L(lang, "Choose amount & payment method", "Выберите сумму и способ оплаты", "בחר סכום ושיטת תשלום"), L(lang, "Enter card details securely", "Введите данные карты безопасно", "הזן פרטי כרטיס בבטחה"), L(lang, "iPoints credited instantly", "iPoints зачисляются сразу", "iPoints מזוכים מיד")]
                : [L(lang, "Choose amount, select Bit", "Выберите сумму, выберите Bit", "בחר סכום ושלם ביט"), L(lang, "Send via Bit with the code in the note", "Отправьте с кодом в примечании", "שלח עם הקוד בהערה"), L(lang, "Upload screenshot — reviewed shortly", "Загрузите скриншот — проверим скоро", "העלה צילום מסך — יאושר בקרוב")]
              ).map((s, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black flex-shrink-0 mt-0.5"
                    style={{ background: "var(--surface-2)", color: "var(--text-secondary)", minWidth: 20 }}>{i + 1}</div>
                  <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>{s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {modal === "success" && (
        <SuccessModal lang={lang} ipoints={modalData.ipoints || 0} amountIls={modalData.amountIls || numericAmount}
          onDone={() => window.location.href = "/dashboard"} />
      )}
      {modal === "pending" && (
        <PendingModal lang={lang} refCode={modalData.refCode} ipoints={modalData.ipoints || 0}
          onDone={() => { setModal(null); resetToStart(); }} />
      )}
      {modal === "error" && (
        <ErrorModal lang={lang} message={modalData.message}
          onRetry={() => { setModal(null); resetToStart(); }}
          onClose={() => setModal(null)} />
      )}
    </div>
  );
}
